import { push, LOCATION_CHANGE } from 'connected-react-router';
import apiUrls from '../../youwell-common/apiUrls';
import ReduxStoreHelper, { LOGOUT } from '../../youwell-common/ReduxStoreHelper';

import { PUBLIC_ACTION_PREFIX as ACTION_PREFIX } from './actions';
import Auth, { authTypes } from '../../youwell-common/Auth';
import {
    parseResponseSaveResult, parseInsertedResponse, buildResponseLookup,
} from '../../youwell-common/responseUtils';

const RESPONSE_INSERTED = 'RESPONSE_INSERTED';
const SESSION_RECEIVED = 'SESSION_RECEIVED';
const SET_CURRENT_PROGRAM = 'SET_CURRENT_PROGRAM';
const END_SESSION = 'END_SESSION';
const COMPLETE_TASK = 'COMPLETE_TASK';
const RESPONSE_SAVED = 'RESPONSE_SAVED';
const TASKVISITED = 'TASKVISITED';
const FEEDBACK_FORM_SENT = 'FEEDBACK_FORM_SENT';

const storeHelper = new ReduxStoreHelper(ACTION_PREFIX);

export const endSessionReasons = {
    tokenExpired: 'tokenExpired',
    timeout: 'timeout',
    manualEnd: 'manualEnd',
    completed: 'completed',
};

export const actionCreators = {
    ...storeHelper.defaultActionCreators(apiUrls.app.public),
    setCurrentProgram: programId => (dispatch) => {
        dispatch({ type: `${ACTION_PREFIX}/${SET_CURRENT_PROGRAM}`, programId });
    },
    updateResponse: data => (dispatch) => {
        dispatch({ type: `${ACTION_PREFIX}/${RESPONSE_INSERTED}`, data });
    },
    sendFirstResponse: (responseData, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.app.public}/re1`,
            body: responseData,
            method: 'POST',
            completeAction: `${ACTION_PREFIX}/${RESPONSE_SAVED}`,
            onData: (data) => {
                Auth.setLoginInfo(data.patientResult?.loginLevel, null, authTypes.public);
            },
            options,
        });
    },
    saveResponse: storeHelper.createInsertActionCreator(`${apiUrls.app.public}/res`, RESPONSE_SAVED),
    getSession: (body, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.app.public}/curr`,
            body,
            method: 'POST',
            completeAction: `${ACTION_PREFIX}/${SESSION_RECEIVED}`,
            onUnauthorized: () => {
                dispatch({ type: END_SESSION, endReason: endSessionReasons.tokenExpired });
                Auth.deauthenticateUser();
            },
            options,
        });
    },
    endSession: (endReason, redirect) => (dispatch) => {
        dispatch({ type: END_SESSION, endReason });
        Auth.deauthenticateUser();
        if (redirect) {
            dispatch(push(redirect));
        }
    },
    completeTask: (data, redirect) => (dispatch) => {
        dispatch({ type: COMPLETE_TASK, data });
        if (redirect) {
            dispatch(push(redirect));
        }
    },
    visitTask: (taskInfo, options) => (dispatch) => {
        storeHelper.fetch(dispatch, {
            url: `${apiUrls.app.program}/vt`,
            method: 'POST',
            body: taskInfo,
            completeAction: `${ACTION_PREFIX}/${TASKVISITED}`,
            options: { background: true, ...options },
        });
    },
    sendFeedbackFromForm: storeHelper.createInsertActionCreator(`${apiUrls.app.public}/feedback`, FEEDBACK_FORM_SENT),
};

const initialState = {
    ...storeHelper.defaultInitialState,
    currentProgramId: null,
    patientId: null,
    patientProgramId: null,
    patientProgramElementId: null,
    endReason: null,
    completedTasks: null,
    latestCompletedStep: -1,
};

export const reducer = (state, action) => {
    switch (action.type) {
    case LOGOUT: {
        return {
            ...initialState,
            details: { ...state.details },
        };
    }
    case LOCATION_CHANGE: {
        if (state.endReason === endSessionReasons.completed) {
            return {
                ...initialState,
                details: { ...state.details },
                currentProgramId: state.currentProgramId,
            };
        }
        return {
            ...state,
        };
    }
    case END_SESSION: {
        return {
            ...initialState,
            details: { ...state.details },
            currentProgramId: state.currentProgramId,
            endReason: action.endReason,
        };
    }
    case COMPLETE_TASK: {
        return {
            ...state,
            latestCompletedStep: Math.max(action.data.stepNr, state.latestCompletedStep),
            completedTasks: {
                ...state.completedTasks,
                [action.data.taskId]: true,
            },
        };
    }
    case `${ACTION_PREFIX}/${SET_CURRENT_PROGRAM}`: {
        return {
            ...state,
            currentProgramId: action.programId,
        };
    }
    case `${ACTION_PREFIX}/${SESSION_RECEIVED}`: {
        if (!action.data) {
            return {
                ...initialState,
                details: { ...state.details },
            };
        }

        return {
            ...state,
            patientId: action.data.patientId,
            patientProgramId: action.data.patientProgramId,
            patientProgramElementId: action.data.patientProgramElementId,
            responses: buildResponseLookup(action.data.responses, state.responses),
        };
    }
    case `${ACTION_PREFIX}/${RESPONSE_SAVED}`: {
        const { inputData, data } = action;
        const { taskResult, patientResult } = data;

        const responses = parseResponseSaveResult(inputData, taskResult, state.responses);

        delete responses.null; // remove responses that is not connected to patientProgramElementId (these are now saved)

        return {
            ...state,
            ...patientResult,
            responses,
        };
    }
    case `${ACTION_PREFIX}/${RESPONSE_INSERTED}`: {
        const {
            programId, moduleId, taskId,
        } = action.data;

        const taskSubtotals = state.details[programId]
            ?.modules.find(m => m.id === moduleId)
            ?.tasks?.find(task => task.id === taskId)
            ?.taskSubtotals || [];

        const responses = parseInsertedResponse(action.data, taskSubtotals, state.responses);

        return {
            ...state,
            responses,
        };
    }
    default: {
        const stateFromDefaultActions = storeHelper.defaultReducer(state, action, initialState);

        if (stateFromDefaultActions !== null) {
            return stateFromDefaultActions;
        }

        return state || initialState;
    }
    }
};
